import React, { useEffect, useState } from 'react'
import {
  CButton,
  CCloseButton,
  CFormInput,
  CLoadingButton,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CSpinner,
} from '@coreui/react-pro'
import CommonService from 'src/service/CommonService'
import ToastComponent from 'src/components/common/TaostComponent'
import { InfoBox } from 'src/components/common/ImportWizard'

// Award points to the players in one round's Team of the Round. `round` is the row from the
// rounds table; the panel is open while it is set.
const TotrPointsForm = ({ round, onClose, onSaved }) => {
  const [loading, setLoading] = useState(false)
  const [loader, setLoader] = useState(false)
  const [rows, setRows] = useState([])
  const [teamStatus, setTeamStatus] = useState(0)
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    if (!round?.round) return
    setLoading(true)
    setRows([])
    setDirty(false)
    CommonService.totwPlayerList(round.round)
      .then((result) => {
        setRows(result.data || [])
        setTeamStatus(result.teamStatus)
      })
      .catch(() => ToastComponent('Could not load the team', 'error'))
      .finally(() => setLoading(false))
  }, [round])

  const setBonus = (index, value) => {
    setDirty(true)
    setRows((current) => current.map((row, i) => (i === index ? { ...row, bonus: value } : row)))
  }

  const save = () => {
    setLoader(true)
    CommonService.updateTotwPlayerPoint({ data: rows })
      .then((res) => {
        if (res.status === 200) {
          ToastComponent(`Round ${round.round} points saved`, 'success')
          onSaved()
          onClose()
        } else {
          ToastComponent(res.message, 'error')
        }
      })
      .catch(() => ToastComponent('Something Went Wrong, Please try again', 'error'))
      .finally(() => setLoader(false))
  }

  const canSave = teamStatus === 1 && rows.length > 0

  return (
    <COffcanvas placement="end" visible={!!round} onHide={onClose} backdrop style={{ width: 480 }}>
      <COffcanvasHeader>
        <div>
          <COffcanvasTitle>Round {round?.round} Player Points</COffcanvasTitle>
          <div className="text-medium-emphasis small">
            Points for each player in this round&apos;s team.
          </div>
        </div>
        <CCloseButton className="text-reset" onClick={onClose} />
      </COffcanvasHeader>
      <COffcanvasBody className="d-flex flex-column">
        <InfoBox className="small">
          These points are awarded to each player for making the Round {round?.round} Team of the
          Round.
        </InfoBox>

        {loading ? (
          <div className="d-flex align-items-center gap-2 text-medium-emphasis py-4">
            <CSpinner size="sm" />
            <span>Loading players...</span>
          </div>
        ) : rows.length === 0 ? (
          <div className="totr-empty">
            No players in this team yet. Add some from <strong>Edit Team</strong>.
          </div>
        ) : (
          <table className="totr-points table align-middle mb-0">
            <thead>
              <tr>
                <th>Player</th>
                <th className="text-end">Points</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={row.id}>
                  <td className="totr-points-name" title={row.player_name}>
                    {row.player_name || 'Player removed'}
                  </td>
                  <td className="text-end">
                    <CFormInput
                      type="number"
                      step="any"
                      size="sm"
                      className="totr-points-input"
                      aria-label={`Points for ${row.player_name}`}
                      name={`data.${index}.bonus`}
                      value={row.bonus ?? ''}
                      placeholder="0"
                      onChange={(e) => setBonus(index, e.target.value)}
                      onFocus={(e) => e.target.select()}
                      onWheel={(e) => e.currentTarget.blur()}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </COffcanvasBody>
      <div className="d-flex justify-content-between gap-2 p-3 border-top">
        <CButton color="secondary" variant="outline" onClick={onClose}>
          Cancel
        </CButton>
        {canSave && (
          <CLoadingButton color="primary" loading={loader} disabled={!dirty} onClick={save}>
            Save Points
          </CLoadingButton>
        )}
      </div>
    </COffcanvas>
  )
}

export default TotrPointsForm
